<template>
  <div class="user-profile-page">
    <div v-if="loading" class="loading">
      <p>Loading profile...</p>
    </div>

    <div v-else-if="error" class="error-message">
      <h2>Error Loading Profile</h2>
      <p>{{ error }}</p>
      <button @click="goHome" class="btn btn-primary">Return to Home</button>
    </div>

    <div v-else-if="user" class="profile-content">
      <!-- Header -->
      <div class="profile-header">
        <button @click="goHome" class="back-button">← Back</button>
        <h1 class="profile-title">My Profile</h1>
      </div>

      <div class="profile-sections">
        <!-- Personal Information Section -->
        <section class="profile-section">
          <div class="section-header">
            <h2 class="section-title">Personal Information</h2>
            <button 
              v-if="!isEditing" 
              @click="startEditing" 
              class="edit-button"
            >
              Edit
            </button>
            <div v-else class="edit-actions">
              <button @click="cancelEditing" class="btn btn-secondary">Cancel</button>
              <button @click="saveChanges" class="btn btn-primary" :disabled="saving">
                {{ saving ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>

          <div v-if="!isEditing" class="info-display">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">First Name:</span>
                <span class="info-value">{{ user.firstName }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Last Name:</span>
                <span class="info-value">{{ user.lastName }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Email:</span>
                <span class="info-value">{{ user.email }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Experience Level:</span>
                <span class="info-value">{{ user.experienceLevel }}</span>
              </div>
              <div v-if="user.professionalRole" class="info-item">
                <span class="info-label">Professional Role:</span>
                <span class="info-value">{{ user.professionalRole === 'rdn' ? 'RDN (Registered Dietitian Nutritionist)' : user.professionalRole === 'ndtr' ? 'NDTR (Nutrition and Dietetics Technician, Registered)' : user.professionalRole }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">City:</span>
                <span class="info-value">{{ user.city }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">State:</span>
                <span class="info-value">{{ user.state }}</span>
              </div>
              <div v-if="user.licenseNumber" class="info-item">
                <span class="info-label">License Number:</span>
                <span class="info-value">{{ user.licenseNumber }}</span>
              </div>
              <div v-if="user.academyNumber" class="info-item">
                <span class="info-label">Academy Number:</span>
                <span class="info-value">{{ user.academyNumber }}</span>
              </div>
            </div>
          </div>

          <form v-else @submit.prevent="saveChanges" class="edit-form">
            <div class="form-grid">
              <div class="form-group">
                <label for="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  v-model="editForm.firstName"
                  required
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  v-model="editForm.lastName"
                  required
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  v-model="editForm.email"
                  required
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="experienceLevel">Experience Level *</label>
                <select
                  id="experienceLevel"
                  v-model="editForm.experienceLevel"
                  required
                  class="form-input"
                >
                  <option value="">Select Experience Level</option>
                  <option value="Entry Level">Entry Level</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>
              <div class="form-group">
                <label for="professionalRole">Professional Role</label>
                <select
                  id="professionalRole"
                  v-model="editForm.professionalRole"
                  class="form-input"
                >
                  <option value="">Select your professional role (optional)</option>
                  <option value="rdn">RDN (Registered Dietitian Nutritionist)</option>
                  <option value="ndtr">NDTR (Nutrition and Dietetics Technician, Registered)</option>
                </select>
              </div>
              <div class="form-group">
                <label for="city">City *</label>
                <input
                  type="text"
                  id="city"
                  v-model="editForm.city"
                  required
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="state">State *</label>
                <input
                  type="text"
                  id="state"
                  v-model="editForm.state"
                  required
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label for="licenseNumber">License Number</label>
                <input
                  type="text"
                  id="licenseNumber"
                  v-model="editForm.licenseNumber"
                  class="form-input"
                  placeholder="Optional"
                />
              </div>
              <div class="form-group">
                <label for="academyNumber">Academy Number</label>
                <input
                  type="text"
                  id="academyNumber"
                  v-model="editForm.academyNumber"
                  class="form-input"
                  placeholder="Optional"
                />
              </div>
            </div>
          </form>
        </section>

        <!-- My Reviews Section -->
        <section class="profile-section reviews-section">
          <div class="section-header">
            <h2 class="section-title">My Reviews</h2>
          </div>

          <div v-if="reviewsLoading" class="reviews-loading">
            <p>Loading reviews...</p>
          </div>

          <div v-else-if="reviews.length === 0" class="no-reviews">
            <p>You haven't submitted any reviews yet.</p>
            <button @click="goToSearch" class="btn btn-primary">Browse Courses</button>
          </div>

          <div v-else>
            <div class="reviews-list">
              <div
                v-for="review in reviews"
                :key="review.id"
                class="review-card"
              >
                <div class="review-header">
                  <div class="review-course-info">
                    <h3 class="review-course-name" @click="viewCourse(review.courseId)">
                      {{ getCourseName(review.courseId) }}
                    </h3>
                    <div v-if="review.createdAt" class="review-date">
                      {{ formatDate(review.createdAt) }}
                    </div>
                  </div>
                  <button
                    @click="handleEditReview(review)"
                    class="edit-review-button"
                    aria-label="Edit review"
                  >
                    Edit
                  </button>
                </div>
                <div class="review-ratings">
                  <div class="review-rating-item">
                    <span class="rating-label">Educational Content:</span>
                    <StarRating :model-value="review.educationalContent" :interactive="false" />
                  </div>
                  <div class="review-rating-item">
                    <span class="rating-label">Engagement:</span>
                    <StarRating :model-value="review.engagement" :interactive="false" />
                  </div>
                  <div class="review-rating-item">
                    <span class="rating-label">Overall Value:</span>
                    <StarRating :model-value="review.overallValue" :interactive="false" />
                  </div>
                </div>
                <div class="review-text">
                  {{ review.reviewText }}
                </div>
              </div>
            </div>

            <!-- Reviews Pagination -->
            <div v-if="reviewsPagination.total_pages > 1" class="reviews-pagination">
              <button
                @click="goToReviewsPage(reviewsPage - 1)"
                :disabled="reviewsPage === 1"
                class="pagination-btn"
              >
                ← Previous
              </button>
              <span class="pagination-info">
                Page {{ reviewsPage }} of {{ reviewsPagination.total_pages }} ({{ reviewsPagination.total }} total)
              </span>
              <button
                @click="goToReviewsPage(reviewsPage + 1)"
                :disabled="reviewsPage === reviewsPagination.total_pages"
                class="pagination-btn"
              >
                Next →
              </button>
            </div>
          </div>
        </section>

        <!-- Provider Claiming Section -->
        <section class="profile-section provider-section">
          <div class="section-header">
            <h2 class="section-title">Provider Account</h2>
          </div>

          <div v-if="!showProviderClaimForm" class="provider-prompt">
            <p v-if="!user.providerId" class="provider-prompt-text">
              Do you represent a provider organization? Claim or create a provider account to manage courses.
            </p>
            <p v-else class="provider-prompt-text">
              You are currently associated with a provider. You can change your provider association below.
            </p>
            <button @click="showProviderClaimForm = true" class="btn btn-primary">
              {{ user.providerId ? 'Change Provider' : 'Claim or Create Provider' }}
            </button>
          </div>

          <div v-else class="provider-claim-form">
            <div class="form-header">
              <h3 class="form-subtitle">Claim or Create Provider</h3>
              <button @click="cancelProviderClaim" class="btn btn-secondary">Cancel</button>
            </div>

            <ProviderSelector
              ref="providerSelector"
              label="Search for Provider"
              :required="false"
              input-id="userProfileProviderSearch"
              @provider-ready="handleProviderReady"
              @provider-cleared="isProviderReady = false"
            />

            <!-- Submit Button -->
            <div class="provider-form-actions">
              <button 
                @click="claimProvider" 
                class="btn btn-primary" 
                :disabled="claimingProvider || !isProviderReady"
              >
                {{ claimingProvider ? 'Processing...' : 'Claim Provider' }}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Add Review Modal -->
    <AddReviewModal
      :show="showAddReviewModal"
      :course-id="editingReviewCourseId"
      :review-id="editingReviewId"
      :existing-review="editingReview"
      @close="handleCloseReviewModal"
      @review-submitted="handleReviewSubmitted"
    />
  </div>
</template>

<script>
import { getCurrentUser, updateUser } from '../../services/userService.js'
import { getReviewsByUserId } from '../../services/reviewService.js'
import { getCoursesPaginated } from '../../services/courseService.js'
import { isAuthenticated } from '../../services/authService.js'
import { claimProvider } from '../../services/providerService.js'
import StarRating from '../shared/StarRating.vue'
import AddReviewModal from '../modals/AddReviewModal.vue'
import ProviderSelector from '../shared/ProviderSelector.vue'

export default {
  name: 'UserProfile',
  components: {
    StarRating,
    AddReviewModal,
    ProviderSelector
  },
  data() {
    return {
      user: null,
      loading: true,
      error: null,
      isEditing: false,
      saving: false,
      editForm: {},
      reviews: [],
      reviewsLoading: false,
      reviewsPagination: {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0
      },
      reviewsPage: 1,
      reviewsPerPage: 20,
      courses: [],
      showAddReviewModal: false,
      editingReviewId: null,
      editingReview: null,
      editingReviewCourseId: null,
      // Provider claiming
      showProviderClaimForm: false,
      claimingProvider: false,
      selectedProviderForClaim: null,
      isProviderReady: false
    }
  },
  async mounted() {
    // Check authentication (route guard should handle this, but double-check)
    if (!isAuthenticated()) {
      this.$router.push({ name: 'landing' })
      return
    }

    await this.loadUser()
    await this.loadReviews()
    await this.loadCourses()
  },
  methods: {
    async loadUser() {
      this.loading = true
      this.error = null
      try {
        this.user = await getCurrentUser()
        if (!this.user) {
          this.error = 'Unable to load user profile. Please sign in again.'
        }
      } catch (err) {
        this.error = 'Failed to load user profile. Please try again.'
        console.error('Error loading user:', err)
      } finally {
        this.loading = false
      }
    },
    async loadReviews() {
      if (!this.user) return
      
      this.reviewsLoading = true
      try {
        const result = await getReviewsByUserId(this.user.id, {
          page: this.reviewsPage,
          perPage: this.reviewsPerPage
        })
        
        // Handle paginated response
        if (result.data && result.pagination) {
          this.reviews = result.data
          this.reviewsPagination = result.pagination
        } else {
          // Fallback for backward compatibility (array response)
          this.reviews = Array.isArray(result) ? result : []
          this.reviewsPagination = {
            page: 1,
            per_page: this.reviews.length,
            total: this.reviews.length,
            total_pages: 1
          }
        }
      } catch (err) {
        console.error('Error loading reviews:', err)
        this.reviews = []
        this.reviewsPagination = {
          page: 1,
          per_page: 0,
          total: 0,
          total_pages: 0
        }
      } finally {
        this.reviewsLoading = false
      }
    },
    goToReviewsPage(page) {
      if (page >= 1 && page <= this.reviewsPagination.total_pages && page !== this.reviewsPage) {
        this.reviewsPage = page
        this.loadReviews()
        // Scroll to reviews section
        this.$nextTick(() => {
          const reviewsSection = this.$el.querySelector('.reviews-section')
          if (reviewsSection) {
            reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },
    async loadCourses() {
      try {
        // Fetch all courses by paginating through all pages
        // This is needed for the course name lookup in reviews
        let allCourses = []
        let page = 1
        let hasMore = true
        const perPage = 100 // Fetch in batches of 100

        while (hasMore) {
          const result = await getCoursesPaginated({ page, perPage })
          allCourses.push(...result.data)
          
          hasMore = page < result.pagination.total_pages
          page++
        }
        
        this.courses = allCourses
      } catch (err) {
        console.error('Error loading courses:', err)
        this.courses = []
      }
    },
    startEditing() {
      this.editForm = {
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        email: this.user.email,
        experienceLevel: this.user.experienceLevel,
        professionalRole: this.user.professionalRole || '',
        city: this.user.city,
        state: this.user.state,
        licenseNumber: this.user.licenseNumber || '',
        academyNumber: this.user.academyNumber || ''
      }
      this.isEditing = true
    },
    cancelEditing() {
      this.isEditing = false
      this.editForm = {}
    },
    async saveChanges() {
      this.saving = true
      try {
        const updatedUser = await updateUser(this.user.id, this.editForm)
        this.user = updatedUser
        this.isEditing = false
        this.editForm = {}
        // Emit event to update user in App.vue
        this.$emit('user-changed')
      } catch (err) {
        console.error('Error saving user:', err)
        // Error modal will be shown by errorHandler
      } finally {
        this.saving = false
      }
    },
    handleEditReview(review) {
      this.editingReviewId = review.id
      this.editingReview = review
      this.editingReviewCourseId = review.courseId
      this.showAddReviewModal = true
    },
    handleCloseReviewModal() {
      this.showAddReviewModal = false
      this.editingReviewId = null
      this.editingReview = null
      this.editingReviewCourseId = null
    },
    async handleReviewSubmitted() {
      await this.loadReviews()
    },
    getCourseName(courseId) {
      const course = this.courses.find(c => c.id === courseId)
      return course ? course.name : 'Unknown Course'
    },
    viewCourse(courseId) {
      this.$router.push({ name: 'view-course', params: { id: courseId } })
    },
    goHome() {
      this.$router.push({ name: 'landing' })
    },
    goToSearch() {
      this.$router.push({ name: 'search' })
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    // Provider claiming methods
    handleProviderReady(provider) {
      this.selectedProviderForClaim = provider
      this.isProviderReady = true
    },
    cancelProviderClaim() {
      this.showProviderClaimForm = false
      if (this.$refs.providerSelector) {
        this.$refs.providerSelector.reset()
      }
      this.selectedProviderForClaim = null
      this.isProviderReady = false
    },
    async claimProvider() {
      if (!this.$refs.providerSelector) {
        return
      }

      this.claimingProvider = true

      try {
        // Get the provider (either selected or create new one)
        const provider = await this.$refs.providerSelector.getProvider()
        
        if (!provider || !provider.id) {
          alert('Please select or create a provider.')
          this.claimingProvider = false
          return
        }

        // Update user with provider ID
        await claimProvider(provider.id)
        this.$emit('user-changed')
        
        // Reset form
        this.cancelProviderClaim()
      } catch (error) {
        console.error('Error claiming provider:', error)
        alert(error.message || 'Failed to claim provider. Please try again.')
      } finally {
        this.claimingProvider = false
      }
    }
  }
}
</script>

<style scoped>
.user-profile-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.loading,
.error-message {
  text-align: center;
  padding: 60px 20px;
  color: var(--color-text-secondary);
}

.error-message h2 {
  color: var(--color-error-dark);
  margin-bottom: 15px;
}

.profile-header {
  margin-bottom: 40px;
}

.back-button {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-size: 1rem;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 20px;
  transition: color var(--transition-normal);
}

.back-button:hover {
  color: var(--color-primary-dark);
}

.profile-title {
  font-size: 2.5rem;
  color: var(--color-text-primary);
  margin: 0;
}

.profile-sections {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.profile-section {
  background: var(--color-surface-light);
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  padding: 30px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--color-border-light);
  flex-wrap: wrap;
  gap: 15px;
}

.section-title {
  font-size: 1.5rem;
  color: var(--color-text-primary);
  margin: 0;
}

.edit-button {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.edit-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 165, 169, 0.3);
}

.edit-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  border: none;
}

.btn-primary {
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 165, 169, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: var(--color-surface-dark);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border-main);
}

.btn-secondary:hover {
  background-color: var(--color-border-light);
}

.info-display {
  margin-top: 20px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-label {
  font-weight: 600;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.info-value {
  color: var(--color-text-primary);
  font-size: 1rem;
}

.edit-form {
  margin-top: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

/* Reviews Section */
.reviews-section {
  margin-top: 20px;
}

.reviews-loading,
.no-reviews {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.review-card {
  background: var(--color-surface-elevated);
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  padding: 25px;
  transition: all var(--transition-normal);
}

.review-card:hover {
  border-color: var(--color-border-main);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  flex-wrap: wrap;
  gap: 15px;
}

.review-course-info {
  flex: 1;
}

.review-course-name {
  font-size: 1.2rem;
  color: var(--color-primary-main);
  margin: 0 0 8px 0;
  cursor: pointer;
  transition: color var(--transition-normal);
}

.review-course-name:hover {
  color: var(--color-primary-dark);
  text-decoration: underline;
}

.review-date {
  color: var(--color-text-light);
  font-size: 0.85rem;
}

.edit-review-button {
  background: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  white-space: nowrap;
}

.edit-review-button:hover {
  background: var(--color-primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.review-ratings {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
}

.review-rating-item {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.rating-label {
  font-weight: 500;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  min-width: 140px;
}

.review-text {
  color: var(--color-text-primary);
  line-height: 1.7;
  font-size: 1rem;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.reviews-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border-light);
}

.reviews-pagination .pagination-btn {
  padding: 10px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all var(--transition-normal);
}

.reviews-pagination .pagination-btn:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

.reviews-pagination .pagination-btn:disabled {
  background-color: var(--color-border-light);
  color: var(--color-text-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.reviews-pagination .pagination-info {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

/* Provider Section */
.provider-section {
  margin-top: 20px;
}

.provider-prompt {
  text-align: center;
  padding: 40px 20px;
}

.provider-prompt-text {
  color: var(--color-text-secondary);
  font-size: 1rem;
  margin-bottom: 20px;
  line-height: 1.6;
}

.provider-claim-form {
  margin-top: 20px;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--color-border-light);
}

.form-subtitle {
  font-size: 1.3rem;
  color: var(--color-text-primary);
  margin: 0;
}

.form-help {
  display: block;
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  margin-top: 4px;
}

.provider-results {
  margin-top: 12px;
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid var(--color-border-main);
  border-radius: 8px;
  background: var(--color-surface-light);
}

.provider-results-header {
  padding: 12px 16px;
  background: var(--color-surface-dark);
  font-weight: 600;
  color: var(--color-text-primary);
  border-bottom: 1px solid var(--color-border-light);
}

.provider-result-item {
  padding: 16px;
  border-bottom: 1px solid var(--color-border-light);
  cursor: pointer;
  transition: all var(--transition-normal);
}

.provider-result-item:last-child {
  border-bottom: none;
}

.provider-result-item:hover {
  background: var(--color-bg-main);
}

.provider-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.provider-details {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.provider-type {
  font-weight: 500;
}

.provider-location {
  color: var(--color-text-light);
}

.provider-results-footer {
  padding: 12px 16px;
  text-align: center;
  border-top: 1px solid var(--color-border-light);
}

.btn-link {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.9rem;
}

.btn-link:hover {
  color: var(--color-primary-dark);
}

.selected-provider {
  margin-top: 12px;
  padding: 16px;
  background: var(--color-surface-elevated);
  border: 2px solid var(--color-primary-main);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.selected-provider-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.provider-type-badge {
  background: var(--color-primary-light);
  color: var(--color-primary-main);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.btn-link-small {
  background: none;
  border: none;
  color: var(--color-primary-main);
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  font-size: 0.85rem;
}

.btn-link-small:hover {
  color: var(--color-primary-dark);
}

.provider-form-section {
  margin-top: 24px;
  padding: 24px;
  background: var(--color-bg-main);
  border-radius: 12px;
  border: 2px solid var(--color-border-light);
}

.section-description {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  margin-top: -8px;
  margin-bottom: 16px;
}

.provider-form-actions {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.form-input.searching {
  border-color: var(--color-primary-main);
}

@media (max-width: 768px) {
  .profile-title {
    font-size: 2rem;
  }

  .info-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .selected-provider {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

